import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ENVIRONMENT from '../../../environments';
import CustomerMainLayout from '../CustomerComponents/CustomerMainLayout';
import CompareSiloView from '../CustomerComponents/AIEditorModalViewer/CompareSiloView';

const getIframeUserContextPayload = () => {
  const keys = [
    'username',
    'company_id',
    'shared_entities',
    'all3d_jwt_token',
    'user_role',
    'is_admin',
    'is_msprovider',
    'managed_company_id',
    'super_admin_username',
    'is_ai_customer',
  ];

  return keys.reduce((acc, key) => {
    const value = localStorage.getItem(key);
    if (value !== null) {
      acc[key] = value;
    }
    return acc;
  }, {});
};

const resolveIframeUrl = (url) => {
  if (!url || typeof url !== 'string') return null;

  try {
    return new URL(url, window.location.origin).toString();
  } catch {
    return null;
  }
};

const MigratedEditAiImage = ({ location }) => {
  const iframeRef = useRef(null);
  const [compareSiloModalOpen, setCompareSiloModalOpen] = useState(false);
  const [compareImageData, setCompareImageData] = useState(null);
  const migratedPortalBaseUrl = (
    ENVIRONMENT.MIGRATED_PORTAL_URL ||
    process.env.REACT_APP_MIGRATED_PORTAL_URL ||
    'http://localhost:5173'
  ).replace(/\/$/, '');
  const iframeSrc = `${migratedPortalBaseUrl}/edit-ai-image${location?.search || ''}`;

  const targetOrigin = useMemo(() => {
    try {
      return new URL(migratedPortalBaseUrl).origin;
    } catch {
      return '*';
    }
  }, [migratedPortalBaseUrl]);

  const postUserContext = useCallback(() => {
    const iframeWindow = iframeRef.current?.contentWindow;
    if (!iframeWindow) return;

    iframeWindow.postMessage(
      {
        type: 'ALL3D_USER_CONTEXT',
        payload: getIframeUserContextPayload(),
      },
      targetOrigin
    );
  }, [targetOrigin]);

  useEffect(() => {
    const onMessage = (event) => {
      if (targetOrigin !== '*' && event.origin !== targetOrigin) return;
      if (event.source !== iframeRef.current?.contentWindow) return;

      const { type, url } = event?.data || {};

      if (type === 'ALL3D_USER_CONTEXT_REQUEST') {
        postUserContext();
        return;
      }

      if (type === 'ALL3D_COMPARE_IMAGE') {
        setCompareImageData(event.data?.payload || null);
        setCompareSiloModalOpen(true);
        return;
      }

      if (type === 'ALL3D_COPY_TO_CLIPBOARD') {
        const text = event.data?.payload?.text;
        if (text) {
          navigator.clipboard.writeText(text).catch(() => {});
        }
        return;
      }

      const resolvedUrl = resolveIframeUrl(url);
      if (!resolvedUrl) return;

      if (type === 'ALL3D_NAVIGATE') {
        window.location.href = resolvedUrl;
        return;
      }

      if (type === 'ALL3D_OPEN_NEW_TAB') {
        window.open(resolvedUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      if (type === 'ALL3D_REPLACE_URL') {
        window.history.replaceState({}, '', resolvedUrl);
        return;
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [postUserContext, targetOrigin]);

  return (
    <CustomerMainLayout selected="26" remove_global_padding={true}>
      <div style={{ width: '100%', height: '100%' }}>
        <iframe
          ref={iframeRef}
          title="Edit AI Image"
          src={iframeSrc}
          onLoad={postUserContext}
          style={{
            width: '100%',
            height: '100%',
            border: '0',
            display: 'block',
          }}
        />
      </div>
      <CompareSiloView
        isOpen={compareSiloModalOpen}
        onClose={() => setCompareSiloModalOpen(false)}
        closeAllModals={() => setCompareSiloModalOpen(false)}
        imageData={compareImageData}
      />
    </CustomerMainLayout>
  );
};

export default MigratedEditAiImage;
